/**
 * 전체 선택(Ctrl/Cmd+A) — 단계 선택 + 블록 선택 모드.
 *
 * 각 블록이 독립 contenteditable 섬이라 네이티브 Ctrl+A는 현재 블록 텍스트만 잡는다.
 * BlockNote 정합 동작:
 *   1단계: 현재 블록 텍스트 전체 선택(아직 전체가 아니면)
 *   2단계: 이미 블록 전체가 선택돼 있으면 → 문서의 모든 최상위 블록 선택(블록 선택 모드)
 *
 * 블록 선택 모드에서:
 *   - Backspace/Delete → 선택 블록 삭제(빈 문단 1개 남김)
 *   - 인쇄 가능한 문자 → 선택 블록 삭제 후 빈 문단에서 입력 이어가기
 *   - Ctrl/Cmd+C·X → 선택 블록 복사/잘라내기(text/html + text/plain)
 *   - 방향키/Escape → 모드 해제(캐럿 배치)
 *   - Ctrl/Cmd+Z·Y → 모드 해제 후 전역 undo/redo에 위임
 *   - 에디터 내 mousedown → 모드 해제
 *
 * 표 셀 내부 Ctrl+A는 table-controls가 소유하므로 관여하지 않는다.
 */

const SELECTED_CLASS = "ml-block-selected";

/**
 * 다중 블록 무손실 클립보드 MIME — 선택 블록의 원본 JSON(저장 폼)을 실어 자기-라운드트립을 보장.
 * (side-menu 드래그 마커 `application/x-lumir-block`(단수)과 혼동 방지 위해 명확히 구분되는 이름 사용)
 */
export const LUMIR_BLOCK_COPY_MIME = "application/x-lumir-block-copy";

/**
 * 무손실 JSON을 text/html 래퍼의 data 속성에 임베드.
 * (비동기 Clipboard API는 커스텀 MIME을 지원하지 않으므로 html로 운반; 붙여넣기 시 회수)
 */
export function embedBlocksInHtml(json, html) {
  return `<div data-lumir-block-copy="${encodeURIComponent(json)}">${html}</div>`;
}

function topLevelBlocks(editorEl) {
  return [...editorEl.children].filter((c) => c.classList && c.classList.contains("block"));
}
function contentOf(blockEl) {
  return blockEl.querySelector(":scope > .content") || blockEl.querySelector(".content");
}
function focusStart(content) {
  if (!content) return;
  content.focus();
  const r = document.createRange();
  r.selectNodeContents(content);
  r.collapse(true);
  const s = window.getSelection();
  s.removeAllRanges();
  s.addRange(r);
}
function focusEnd(content) {
  if (!content) return;
  content.focus();
  const r = document.createRange();
  r.selectNodeContents(content);
  r.collapse(false);
  const s = window.getSelection();
  s.removeAllRanges();
  s.addRange(r);
}

/** 현재 선택이 content 텍스트 전체를 덮는지(양끝 여백 없음) */
function isWholeContentSelected(content) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return false;
  const r = sel.getRangeAt(0);
  if (r.collapsed) return false;
  if (!content.contains(r.startContainer) || !content.contains(r.endContainer)) return false;
  const pre = r.cloneRange();
  pre.selectNodeContents(content);
  pre.setEnd(r.startContainer, r.startOffset);
  const post = r.cloneRange();
  post.selectNodeContents(content);
  post.setStart(r.endContainer, r.endOffset);
  const empty = (content.textContent || "").length === 0;
  return empty || (pre.toString() === "" && post.toString() === "");
}

function selectWholeContent(content) {
  const r = document.createRange();
  r.selectNodeContents(content);
  const s = window.getSelection();
  s.removeAllRanges();
  s.addRange(r);
}

export function attachBlockSelect(editorEl, opts) {
  opts = opts || {};
  const isEditable = typeof opts.isEditable === "function" ? opts.isEditable : () => true;
  const isComposing = typeof opts.isComposing === "function" ? opts.isComposing : () => false;
  const getDocument = opts.getDocument || (() => []);
  const htmlOf = opts.htmlOf || (() => "");
  const textOf = opts.textOf || (() => "");
  const jsonOf = typeof opts.jsonOf === "function" ? opts.jsonOf : () => "";
  const makeParagraphEl = opts.makeParagraphEl || (() => null);
  const commitChange = typeof opts.commitChange === "function" ? opts.commitChange : () => {};

  let selected = []; // 선택된 최상위 블록 요소들
  let active = false;

  function clear() {
    for (const b of selected) b.classList.remove(SELECTED_CLASS);
    selected = [];
    active = false;
  }

  function selectAllBlocks() {
    clear();
    selected = topLevelBlocks(editorEl);
    if (!selected.length) return;
    for (const b of selected) b.classList.add(SELECTED_CLASS);
    active = true;
    const sel = window.getSelection(); // 네이티브 텍스트 하이라이트는 접어 블록 하이라이트만 남김
    if (sel) sel.removeAllRanges();
  }

  /** 선택 블록 → 저장 blocks 배열(순서 보존) */
  function selectedBlockObjects() {
    const all = topLevelBlocks(editorEl);
    const doc = getDocument();
    const idxs = selected.map((b) => all.indexOf(b)).filter((i) => i >= 0).sort((a, b) => a - b);
    return idxs.map((i) => doc[i]).filter(Boolean);
  }

  function deleteSelected() {
    if (!selected.length) return;
    const all = topLevelBlocks(editorEl);
    const ordered = selected.slice().sort((a, b) => all.indexOf(a) - all.indexOf(b));
    const para = makeParagraphEl();
    const first = ordered[0];
    if (para) first.replaceWith(para);
    else first.remove();
    for (const b of ordered.slice(1)) b.remove();
    clear();
    if (para) focusStart(contentOf(para));
    commitChange();
  }

  function onKeydown(e) {
    if (!isEditable()) return;
    if (e.isComposing || isComposing()) return;
    const mod = e.metaKey || e.ctrlKey;
    const key = e.key;

    // Ctrl/Cmd+A 단계 선택
    if (mod && !e.altKey && (key === "a" || key === "A")) {
      if (e.target.closest && e.target.closest(".lumir-tbl")) return; // 표는 table-controls 소관
      const content = e.target.closest && e.target.closest('.content[contenteditable="true"]');
      if (active) {
        e.preventDefault();
        return;
      } // 이미 전체 선택 상태 유지
      if (!content || !editorEl.contains(content)) return;
      e.preventDefault();
      const empty = (content.textContent || "").length === 0;
      // 빈 블록이거나 이미 블록 전체가 선택됐으면 → 전체 블록 선택, 아니면 현재 블록만
      if (empty || isWholeContentSelected(content)) selectAllBlocks();
      else selectWholeContent(content);
      return;
    }

    if (!active) return; // 이하 블록 선택 모드 전용

    // undo/redo는 전역 핸들러에 위임(모드만 해제 — restore가 DOM 재구축)
    if (mod && !e.altKey && (key === "z" || key === "Z" || key === "y" || key === "Y")) {
      clear();
      return;
    }
    // 복사/잘라내기 — copy 이벤트에 의존하지 않고 직접 기록(selectAllBlocks가 네이티브 선택을 비워 실제
    // Chrome은 copy 이벤트를 발화하지 않으므로). copyBlocks 내부에서 async clipboard/execCommand 처리.
    if (mod && !e.altKey && (key === "c" || key === "C" || key === "x" || key === "X")) {
      e.preventDefault();
      copyBlocks(key === "x" || key === "X");
      return;
    }

    if (key === "Escape") {
      e.preventDefault();
      const first = selected[0];
      clear();
      focusStart(contentOf(first));
      return;
    }
    if (key === "ArrowUp" || key === "ArrowLeft") {
      e.preventDefault();
      const first = selected[0];
      clear();
      focusStart(contentOf(first));
      return;
    }
    if (key === "ArrowDown" || key === "ArrowRight") {
      e.preventDefault();
      const last = selected[selected.length - 1];
      clear();
      focusEnd(contentOf(last));
      return;
    }
    if (key === "Backspace" || key === "Delete") {
      e.preventDefault();
      deleteSelected();
      return;
    }
    // 인쇄 가능한 단일 문자 → 선택 삭제 후 빈 문단에서 입력 이어가기(문자는 네이티브 입력)
    if (!mod && !e.altKey && key && key.length === 1) {
      deleteSelected();
      return; // preventDefault 안 함 → 방금 포커스한 빈 문단에 문자 입력
    }
  }

  /**
   * 블록 선택 모드에서 Ctrl/Cmd+C·X 직접 처리.
   * selectAllBlocks가 네이티브 선택을 비우므로 실제 Chrome은 copy 이벤트를 발화하지 않는다(직전 클립보드가
   * 그대로 붙음). 그래서 이벤트에 의존하지 않고 비동기 Clipboard API로 직접 기록한다(무손실 JSON은 html에
   * 임베드 → 커스텀 MIME 불요). 미지원/차단 시 임시 range + execCommand('copy')로 폴백(→ onClipboard가 기록).
   * (표 table-controls.copySelectedCells와 동일 전략)
   */
  function copyBlocks(cut) {
    const blocks = selectedBlockObjects();
    if (!blocks.length) return;
    const json = jsonOf(blocks);
    const html = embedBlocksInHtml(json, htmlOf(blocks));
    const text = textOf(blocks);
    try {
      if (
        typeof navigator !== "undefined" && navigator.clipboard &&
        typeof navigator.clipboard.write === "function" && typeof window.ClipboardItem === "function"
      ) {
        const item = new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        });
        navigator.clipboard.write([item]).then(() => { if (cut) deleteSelected(); }, () => {});
        return; // cut 삭제는 쓰기 성공 후에만(데이터 유실 방지)
      }
    } catch (_) {}
    // 폴백: 선택 블록에 네이티브 range를 만들어 execCommand('copy') → onClipboard가 3종 MIME 기록.
    let ok = false;
    try {
      const first = selected[0], last = selected[selected.length - 1];
      if (first && last && typeof document.execCommand === "function") {
        const sel = window.getSelection();
        const range = document.createRange();
        range.setStartBefore(first);
        range.setEndAfter(last);
        sel.removeAllRanges();
        sel.addRange(range);
        ok = document.execCommand("copy"); // 항상 copy로 발화(→onClipboard가 기록); cut 삭제는 아래서 처리(이중삭제 방지)
        sel.removeAllRanges(); // 하이라이트 전용 상태 복원
      }
    } catch (_) {}
    if (cut && ok) deleteSelected();
  }

  function onClipboard(e, cut) {
    if (!active) return;
    const blocks = selectedBlockObjects();
    if (!blocks.length) return;
    // 무손실 lumir-JSON을 '먼저' 기록(자기-라운드트립 우선), html/plain은 외부 앱 폴백. 각 setData는 독립 try/catch.
    let wroteLossless = false;
    const cd = e.clipboardData;
    if (cd && cd.setData) {
      try { cd.setData(LUMIR_BLOCK_COPY_MIME, jsonOf(blocks)); wroteLossless = true; } catch (_) {}
      try { cd.setData("text/html", htmlOf(blocks)); } catch (_) {}
      try { cd.setData("text/plain", textOf(blocks)); } catch (_) {}
    }
    e.preventDefault();
    if (cut && wroteLossless) deleteSelected(); // 무손실 기록 성공 시에만 삭제(쓰기 실패 시 원본 보존)
  }
  const onCopy = (e) => onClipboard(e, false);
  const onCut = (e) => onClipboard(e, true);
  const onMousedown = () => {
    if (active) clear();
  };

  editorEl.addEventListener("keydown", onKeydown);
  editorEl.addEventListener("copy", onCopy);
  editorEl.addEventListener("cut", onCut);
  editorEl.addEventListener("mousedown", onMousedown);

  /** 선택된 최상위 블록들의 편집 가능 .content(일반 텍스트 블록만; 표/단 래퍼 제외) — 크로스 서식용. */
  function selectedIslands() {
    return selected
      .map((b) => b.querySelector(':scope > .content[contenteditable="true"]'))
      .filter(Boolean);
  }

  return {
    clear,
    isActive: () => active,
    selectAll: selectAllBlocks,
    selectedIslands,
    destroy() {
      editorEl.removeEventListener("keydown", onKeydown);
      editorEl.removeEventListener("copy", onCopy);
      editorEl.removeEventListener("cut", onCut);
      editorEl.removeEventListener("mousedown", onMousedown);
    },
  };
}
